import { useAppDispatch, useAppSelector } from "@app/hooks";
import { Loader } from "@components/Loader";
import { ChangeResultStatusBox } from "@fadmin/components/ChangeResultStatusBox";
import { getUIResultsFromAPIResults } from "@helpers/scoringHelper";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { useApi } from "@services/useApi";
import { setAssessmentResults } from "@store/assessmentResultSlice";
import { AssessmentStatus, ExistingExamineeDetails } from "@store/generalTypes";
import { Colors } from "@styles/colors";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useEffect, useRef, useState } from "react";
import { Translate } from "react-i18nify";
import { useParams } from "react-router";
import { AssessmentDetailsHeader } from "../components/assessmentDetailsHeader";
import { AssessmentNotFound } from "../components/assessmentNotFound";
import AudioPlayer, { Track } from "../components/AudioPlayer/AudioPlayer";
import { NotesSection } from "../components/notesSection";
import { PerformanceReport } from "../components/performanceReport";
import { PresentTargetResults } from "../components/presentTargetResults";
import { AssessmentDisclaimer } from "../components/AssessmentDisclaimer";
import { addReportToPdf, prepareReportForPdf } from "../components/reportPdf";

export const ResultsDetails = (): JSX.Element => {
    const { id } = useParams();

    if (!id) {
        return <AssessmentNotFound />;
    }

    const [isLoading, setIsLoading] = useState(true);
    const [examinee, setExaminee] = useState<ExistingExamineeDetails>({} as ExistingExamineeDetails);
    const [showAudioPlayer, setShowAudioPlayer] = useState(false);
    const [currentAudioStep, setCurrentAudioStep] = useState<string>();
    const [audioTracks, setAudioTracks] = useState<Track[]>();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [startingIndex, setStartingIndex] = useState(0);
    const [audioClicked, setAudioClicked] = useState<number[][]>([]);

    const { assessmentResult, sections, location } = useAppSelector((x) => {
        return {
            assessmentResult: x.assessmentResult,
            sections: getUIResultsFromAPIResults(x.assessmentResult),
            location: x.orgLocations.currentLocation
        };
    });
    const dispatch = useAppDispatch();
    const { getAssessmentResults, getExamineeDetails, updateAssessmentStatus, reviewAssessment } = useApi();

    const { isClinician, isClinicalAdmin } = useUserPermission({ useCurrentLocation: true });

    const loadAssessmentResult = async (): Promise<void> => {
        if (location) {
            const apiAssessmentResults = await getAssessmentResults(location.id, Number(id));
            dispatch(setAssessmentResults(apiAssessmentResults));

            const examineeDetails = await getExamineeDetails(location.id, apiAssessmentResults.examineeId);
            setExaminee(examineeDetails);

            setIsLoading(false);
        }
    };

    const toggleAudioPlayer = (): void => {
        setShowAudioPlayer(!showAudioPlayer);
    };

    const handleOnTrackPlayed = (trackIndex: number): void => {
        const currentIndexAudioClicked = audioClicked[currentStep] ?? [];

        if (currentIndexAudioClicked?.some((c) => c === trackIndex)) return;
        const clicked = [...currentIndexAudioClicked];
        clicked.push(trackIndex);

        audioClicked[currentStep] = clicked;

        setAudioClicked([...audioClicked]);
    };

    const showAudioPlayerForStep = (stepIndex: number, audioStep: string, tracks: Track[], index?: number): void => {
        setShowAudioPlayer(false);
        setTimeout(() => {
            setAudioTracks(tracks);
            setCurrentStep(stepIndex);
            setCurrentAudioStep(audioStep);
            setShowAudioPlayer(true);
            setStartingIndex(index ?? 0);
        }, 60);
    };

    const printRef = useRef<HTMLDivElement>(null);
    const handleDownloadToPdf = async (): Promise<void> => {
        if (!printRef.current) return;
        const element = printRef.current;
        const canvas = await html2canvas(element, {
            windowWidth: 1400,
            scale: 2,
            onclone: (doc, clonedElement) => {
                const eles = doc.getElementsByClassName("is-hidden");
                if (eles) {
                    const elements = Array.from(eles);
                    elements.forEach((e) => e.classList.remove("is-hidden"));
                }
                prepareReportForPdf(clonedElement);
            }
        });
        const data = canvas.toDataURL("image/png");

        // eslint-disable-next-line new-cap
        const pdf = new jsPDF();
        addReportToPdf(pdf, data);
        pdf.save(assessmentResult.examineeId.toString() + "-" + assessmentResult.visitNumber.toString() + ".pdf");
    };

    const handleChangeStatus = async (note: string): Promise<void> => {
        await updateAssessmentStatus(
            location!.id,
            assessmentResult.id,
            assessmentResult.status.toLowerCase() === AssessmentStatus.ReviewNeeded.replace(" ", "").toLowerCase()
                ? AssessmentStatus.ReviewComplete
                : AssessmentStatus.ReviewNeeded
        );

        await reviewAssessment(location!.id, assessmentResult.id, note);

        return await loadAssessmentResult();
    };

    useEffect(() => {
        void loadAssessmentResult();
    }, []);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div>
            {assessmentResult.id === 0 ? (
                <AssessmentNotFound />
            ) : (
                <div>
                    <div ref={printRef}>
                        <AssessmentDetailsHeader
                            assessment={assessmentResult}
                            examinee={examinee}
                            handlePrint={handleDownloadToPdf}
                            handleChangeStatus={handleChangeStatus}
                        />
                        <PerformanceReport apiScore={assessmentResult.result} />
                        <AssessmentDisclaimer />
                    </div>
                    <NotesSection notes={assessmentResult.notes!} />
                    {sections.map((r, i) => (
                        <PresentTargetResults
                            key={i}
                            stepIndex={i}
                            section={r}
                            isRecall={r.isRecall}
                            showAudioPlayerForStep={showAudioPlayerForStep}
                            audioClicked={audioClicked[i]}
                        />
                    ))}
                    {(isClinicalAdmin || isClinician) && (
                        <ChangeResultStatusBox
                            locationId={location!.id}
                            assessmentId={assessmentResult.id}
                            status={assessmentResult.status}
                            refresh={loadAssessmentResult}
                        />
                    )}
                    {assessmentResult.status === AssessmentStatus.Invalidated.toLowerCase() && (
                        <a
                            style={{
                                position: "absolute",
                                fontSize: "12vw",
                                top: "40%",
                                left: "24vw",
                                opacity: "10%",
                                transform: "rotate(-34.45deg)",
                                color: Colors.errorRed,
                                fontWeight: 700,
                                textAlign: "center",
                                cursor: "default"
                            }}
                        >
                            <Translate value="admin.invalidated" />
                        </a>
                    )}
                </div>
            )}

            {showAudioPlayer && audioTracks && !!currentAudioStep && (
                <div style={{ position: "sticky", bottom: 0 }}>
                    <div
                        style={{
                            backgroundColor: "#415C66",
                            height: 150,
                            alignItems: "center",
                            display: "flex",
                            justifyContent: "center"
                        }}
                    >
                        <AudioPlayer
                            tracks={audioTracks}
                            stepTitle={currentAudioStep}
                            onTrackPlayed={handleOnTrackPlayed}
                            startingIndex={startingIndex}
                        />
                        <a
                            style={{ position: "absolute", right: "1%", top: "3%", color: "#fff" }}
                            className="has-text-weight-bold"
                            onClick={toggleAudioPlayer}
                        >
                            X
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};
